import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, X } from 'lucide-react';
import { getLeaderboard } from '../services/api-client';
import { Difficulty, GameMode } from '../types';

interface LeaderboardEntry {
  userId: string;
  userName: string;
  userPicture: string;
  wins: number;
  lastWinTimestamp: number;
  username?: string; // Custom username
}

interface LeaderboardProps {
  isDarkMode: boolean;
  onClose: () => void;
}

export const Leaderboard: React.FC<LeaderboardProps> = ({ isDarkMode, onClose }) => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getLeaderboard({ limit: 50 });
      setEntries(result.entries || []);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setError('Failed to load leaderboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return <Trophy className="text-yellow-500" size={20} />;
    if (index === 1) return <Medal className="text-gray-400" size={20} />;
    if (index === 2) return <Award className="text-orange-600" size={20} />;
    return <span className={`font-bold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>#{index + 1}</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={`w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col ${
        isDarkMode ? 'bg-gray-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <Trophy className={isDarkMode ? 'text-yellow-400' : 'text-yellow-600'} size={32} />
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
              Leaderboard
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' 
                : 'hover:bg-gray-100 text-gray-600 hover:text-gray-800'
            }`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Filters */}
        <div className={`p-4 border-b flex gap-4 ${
          isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex-1">
            <label className={`block text-xs font-medium mb-1 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Sort By
            </label>
            <select
              value="all"
              disabled
              className={`w-full px-3 py-2 rounded-lg text-sm border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-200' 
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="all">All Time</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Loading leaderboard...
            </div>
          ) : error ? (
            <div className={`text-center py-12 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              {error}
            </div>
          ) : entries.length === 0 ? (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <Trophy size={48} className="mx-auto mb-4 opacity-50" />
              <p>No wins recorded yet.</p>
              <p className="text-sm mt-2">Be the first to complete a game!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry, index) => (
                <div
                  key={`${entry.userId}-${entry.lastWinTimestamp}`}
                  className={`flex items-center gap-4 p-4 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-800 hover:bg-gray-750' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {/* Rank */}
                  <div className="w-8 flex items-center justify-center">
                    {getRankIcon(index)}
                  </div>

                  {/* User Info */}
                  <img
                    src={entry.userPicture}
                    alt={entry.username || entry.userName}
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium truncate ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {entry.username || entry.userName}
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      <span>Total Wins</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-right">
                    <div className={`font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {entry.wins} {entry.wins === 1 ? 'win' : 'wins'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t text-center text-xs ${
          isDarkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-500'
        }`}>
          Showing top {entries.length} players • Ranked by total wins
        </div>
      </div>
    </div>
  );
};
